import React, { useMemo } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './chartTheme';

/**
 * Battery Energy Consumption (§6 plot 15): cumulative energy consumption over the
 * selected drive cycle. Plotted with Time on the X-axis (not Vehicle Speed as the raw
 * spec table literally lists) since cumulative energy is monotonically increasing and
 * vehicle speed is not — plotting it against a non-monotonic axis produces an unreadable
 * chart. Vehicle speed is shown instead as a secondary overlay trace on its own axis, per
 * product decision.
 */
const BatteryEnergyConsumptionChart = ({ batterySeries }) => {
  const zoom = useChartZoom();
  const data = useMemo(() => {
    if (!batterySeries) return [];
    return batterySeries.time_s.map((t, i) => ({
      time_s: t,
      cumulative_energy_kwh: batterySeries.cumulative_energy_kwh[i],
      speed_kph: batterySeries.speed_kph[i],
    }));
  }, [batterySeries]);

  if (data.length === 0) return null;

  return (
    <ChartCard
      icon={TrendingUp}
      title="Battery Energy Consumption"
      description="Cumulative battery energy consumption over time along the selected drive cycle, with vehicle speed overlaid."
      testId="chart-battery-energy-consumption"
    >
      <div>
        <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-energy" />
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart
            data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
          >
            <CartesianGrid stroke={CHART_GRID} vertical={false} />
            <XAxis
              dataKey="time_s" type="number" domain={zoom.xDomain} allowDataOverflow
              tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
              label={{ value: 'Time (s)', position: 'insideBottom', offset: -4, style: AXIS_LABEL_STYLE }}
            />
            <YAxis
              yAxisId="energy" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
              label={{ value: 'Energy (kWh)', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }} width={64}
            />
            <YAxis
              yAxisId="speed" orientation="right" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
              label={{ value: 'Speed (km/h)', angle: 90, position: 'insideRight', style: AXIS_LABEL_STYLE }} width={56}
            />
            <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} formatter={(v) => Number(v).toFixed(2)} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Line yAxisId="energy" type="monotone" dataKey="cumulative_energy_kwh" name="Cumulative Energy (kWh)" stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
            <Line yAxisId="speed" type="monotone" dataKey="speed_kph" name="Vehicle Speed (km/h)" stroke={SERIES_COLORS[3]} dot={false} strokeWidth={1.25} isAnimationActive={false} />
            {zoom.isSelecting && (
              <ReferenceArea yAxisId="energy" x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

export default BatteryEnergyConsumptionChart;
